/* eslint-disable consistent-return */
import _ from 'lodash';
import { createSelector } from 'reselect';
import {
  getModules,
  getLessons,
  getCurrentModuleSlug,
  getCurrentTopicSlug,
  getCurrentLessonSlug,
  getCurrentPageNumber,
  getDischargeSteps,
  getLessonPages
} from './core';
import {
  getCurrentModule,
  getDischargeModuleForCurrentLocale
} from './modules';

const getLessonsWithOrderedPages = createSelector(
  getLessons,
  getLessonPages,
  (lessons, lessonPages) =>
    _.mapValues(lessons, lesson => ({
      ...lesson,
      pages: _.chain(lesson.pages)
        .map(pageId => lessonPages[pageId])
        .sortBy('displayOrder')
        .value()
    }))
);

// Name is a bit inaccurate but couldn't think of better one. Since lessons can have the same
// slugs as other lessons we can't create a global lessonSlugToLessonMap. Therefore this returns
// a lessonSlugToLessonMap for each module, keyed by module slug.
const getLessonSlugToLessonMap = createSelector(
  getLessons,
  getModules,
  (lessons, modules) =>
    _.reduce(
      lessons,
      (result, lesson) => {
        const module = modules[lesson.moduleId];
        const newResult = { ...result };

        _.set(newResult, `${module.slug}.${lesson.slug}`, lesson);
        return newResult;
      },
      {}
    )
);

const getLessonsForCurrentModule = createSelector(
  getCurrentModule,
  currentModule => {
    if (typeof currentModule !== 'undefined') {
      return (
        // _.filter(lesson => lesson.moduleId === currentModule.id)
        // .sortBy('displayOrder')
        // .value()
        _.orderBy(currentModule.lessons, 'menu_order')
      );
    }
  }
);

const getCurrentLesson = createSelector(
  getLessonsForCurrentModule,
  getCurrentLessonSlug,
  getCurrentTopicSlug,
  (currentLessons, lessonSlug, topicSlug) => {
    if (typeof currentLessons !== 'undefined') {
      const thisLesson = currentLessons[parseInt(lessonSlug, 10) - 1];
      const thisTopic = thisLesson && thisLesson.topics[topicSlug];
      return (
        thisTopic && {
          ...thisTopic,
          lastTopic: parseInt(topicSlug, 10) === thisLesson.topics.length - 1
        }
      );
    }
  }
);

const getCurrentDischargeStep = createSelector(
  getDischargeSteps,
  getCurrentModuleSlug,
  (currentLessons, lessonSlug) => {
    if (typeof currentLessons !== 'undefined') {
      return (
        currentLessons[parseInt(lessonSlug, 10) - 1] &&
        currentLessons[parseInt(lessonSlug, 10) - 1]
      );
    }
  }
);

const getNextTopic = createSelector(
  getLessonsForCurrentModule,
  getCurrentLessonSlug,
  getCurrentTopicSlug,
  (currentLessons, lessonSlug, topicSlug) => {
    if (typeof currentLessons !== 'undefined') {
      return (
        currentLessons[parseInt(lessonSlug, 10) - 1] &&
        currentLessons[parseInt(lessonSlug, 10) - 1].topics[
          parseInt(topicSlug, 10) + 1
        ]
      );
    }
  }
);

const getNextStep = createSelector(
  getDischargeSteps,
  getCurrentModuleSlug,
  getCurrentPageNumber,
  (currentLessons, lessonSlug, pageNumber) => {
    if (typeof currentLessons !== 'undefined') {
      let result = {};
      if (typeof currentLessons[parseInt(lessonSlug, 10) - 1] !== 'undefined') {
        result =
          currentLessons[parseInt(lessonSlug, 10) - 1].topics[
            parseInt(pageNumber, 10) + 1
          ];
      }
      return result;
    }
  }
);

// prev topic for learning path navigation
const getPrevTopic = createSelector(
  getLessonsForCurrentModule,
  getCurrentLessonSlug,
  getCurrentTopicSlug,
  (currentLessons, lessonSlug, topicSlug) => {
    const lessonIndex = parseInt(lessonSlug, 10) - 1;
    if (typeof currentLessons !== 'undefined') {
      const topicIndex = parseInt(topicSlug, 10) - 1;
      // first topic of lesson should point back to last topic of previous lesson.
      // except for first lesson
      if (lessonIndex > 0 && topicIndex === -1) {
        const prevLessonLastTopicIndex =
          currentLessons[lessonIndex - 1].topics.length - 1;
        return (
          currentLessons[lessonIndex - 1] &&
          currentLessons[lessonIndex - 1].topics[prevLessonLastTopicIndex]
        );
      }
      return (
        currentLessons[lessonIndex] &&
        currentLessons[lessonIndex].topics[topicIndex]
      );
    }
    return {};
  }
);

// prev step for discharge path navigation
const getPrevStep = createSelector(
  getDischargeSteps,
  getCurrentModuleSlug,
  getCurrentPageNumber,
  (currentLessons, lessonSlug, pageNumber) => {
    if (typeof currentLessons !== 'undefined') {
      let result = {};
      const lessonIndex = parseInt(lessonSlug, 10) - 1;

      if (typeof currentLessons[lessonIndex] !== 'undefined') {
        const topicIndex = parseInt(pageNumber, 10) - 1;
        result = currentLessons[lessonIndex].topics[topicIndex];
        // first topic of step should redirect to previous step
        if (!result && lessonIndex > 0) {
          const prevStepLastTopicIndex =
            currentLessons[lessonIndex - 1].topics.length - 1;
          result =
            currentLessons[lessonIndex - 1].topics[prevStepLastTopicIndex];
        }
      }
      return result;
    }
  }
);

const getFirstLessonForCurrentModule = createSelector(
  getLessonsForCurrentModule,
  lessons => _.find(lessons, lesson => lesson.displayOrder === 1) || {}
);

const getLessonIdToPageCountMap = createSelector(
  getLessons,
  lessons => _.mapValues(lessons, lesson => lesson.pages.length)
);

const getDischargeLessons = createSelector(
  getLessons,
  getDischargeModuleForCurrentLocale,
  (allLessons, dischargeModule) =>
    _.chain(allLessons)
      .filter(lesson => lesson.moduleId === dischargeModule.id)
      .sortBy('displayOrder')
      .value()
);

export {
  getLessonsWithOrderedPages,
  getLessonSlugToLessonMap,
  getCurrentLesson,
  getNextTopic,
  getPrevTopic,
  getLessonsForCurrentModule,
  getFirstLessonForCurrentModule,
  getLessonIdToPageCountMap,
  getDischargeLessons,
  getCurrentDischargeStep,
  getNextStep,
  getPrevStep
};


// WEBPACK FOOTER //
// ./src/selectors/lessons.js